import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TSTitleCard } from '@/components/shared/card/Card';
import { secondsToTimeString } from '@/helpers/dateHelper';
import { useEmployeeSchedule } from '@/services/employee/schedule/useEmployeeSchedule';
import { useLeaveTypes } from '@/services/leaveManagement/LeaveTypesProvider';
import { strings } from '@/services/translation/strings';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';

interface PatternTimeDisplayProps {
  label: string;
  actualSeconds: number | undefined | null;
  patternSeconds: number | undefined | null;
}

const PatternTimeDisplay: FC<PatternTimeDisplayProps> = ({ label, actualSeconds, patternSeconds }) => {
  const hasActual = actualSeconds != null;
  const hasPattern = patternSeconds != null;
  const isDifferent = hasActual && hasPattern && actualSeconds !== patternSeconds;

  return (
    <div>
      <p className="font-semibold">{label}</p>
      <div className="flex items-center gap-2">
        {isDifferent && <span className="text-gray-400 line-through">{secondsToTimeString(patternSeconds)}</span>}
        <span>{hasActual ? secondsToTimeString(actualSeconds) : null}</span>
      </div>
    </div>
  );
};

interface TeamMemberCalendarDayViewAvailabilityCardProps {
  dateObj: Date;
  date: string;
  userId: string;
}

export const TeamMemberCalendarDayViewAvailabilityCard: FC<TeamMemberCalendarDayViewAvailabilityCardProps> = ({
  dateObj,
  date,
  userId
}) => {
  const leaveTypes = useLeaveTypes();

  const data = useEmployeeSchedule(userId, dateObj, dateObj);
  const schedule = data.schedule[date];
  const availableTimes = schedule?.availableTimes[0];

  const leaveType = useMemo(() => {
    if (!leaveTypes) return null;
    const scheduleLeaveType = schedule?.leaveType;
    return scheduleLeaveType ? leaveTypes.find(lt => lt.id === scheduleLeaveType) : null;
  }, [leaveTypes, schedule]);

  const navigate = useNavigate();

  const onEditClick = () => {
    navigate(`edit-availability`);
  };

  return (
    <TSTitleCard
      title={format(dateObj, 'EEEE do MMMM yyyy')}
      primaryAction={{
        label: strings.timeshift.teamMember.calendarDayView.editAvailability.title,
        onClick: onEditClick
      }}
      descriptor={strings.timeshift.teamMember.calendarDayView.availability}
      icon={AiOutlineCalendar}
    >
      <SubmissionErrorBoundary>
        <div className="flex flex-col space-y-5">
          <div>
            <p className="font-semibold">{strings.timeshift.teamMember.calendarDayView.employeeStatus}</p>
            <p>{leaveType?.name ?? strings.common.available}</p>
          </div>
          {!leaveType && (
            <div className="flex flex-row space-x-28">
              <PatternTimeDisplay
                label={strings.timeshift.teamMember.calendarDayView.availableFrom}
                actualSeconds={availableTimes?.start}
                patternSeconds={schedule?.patternStart}
              />
              <PatternTimeDisplay
                label={strings.timeshift.teamMember.calendarDayView.availableUntil}
                actualSeconds={availableTimes?.end}
                patternSeconds={schedule?.patternEnd}
              />
            </div>
          )}
          <div>
            <p className="font-semibold">
              {strings.timeshift.teamMember.calendarDayView.usualWorkingPattern(format(dateObj, 'EEEE'))}
            </p>
            <p>
              {secondsToTimeString(schedule?.patternStart ?? 0)} – {secondsToTimeString(schedule?.patternEnd ?? 0)}
            </p>
          </div>
        </div>
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};
